"use client"

import { useMemo, JSX } from "react"
import { graphStratify, sugiyama, decrossOpt, decrossTwoLayer, coordCenter } from "d3-dag"
import { HpcJob, JobStatus } from "@/types/hpc-jobs"
import { getStatusVariant } from "@/lib/hpc-jobs-utils"

interface WorkflowDAGProps {
  /** Jobs in the workflow to visualize as a DAG. */
  jobs: HpcJob[]
}

/**
 * Represents a node in the DAG with its computed position.
 * Coordinates are in the final horizontal layout space:
 * - x increases left-to-right (depth/layer)
 * - y increases top-to-bottom (position within layer)
 */
interface DagNode {
  job: HpcJob
  x: number
  y: number
}

/**
 * Represents an edge between two nodes in the DAG.
 * Coordinates are in the final horizontal layout space.
 */
interface DagEdge {
  sourceId: string
  targetId: string
  sourceX: number
  sourceY: number
  targetX: number
  targetY: number
}

/**
 * Layout configuration constants.
 * HORIZONTAL_GAP: space between columns (depth layers)
 * VERTICAL_GAP: space between rows (nodes in same layer)
 */
const NODE_WIDTH = 140
const NODE_HEIGHT = 50
const HORIZONTAL_GAP = 60
const VERTICAL_GAP = 30
const PADDING = 40

/**
 * Node-count threshold that selects the DAG decrossing (edge-crossing
 * minimization) strategy.
 *
 * d3-dag's optimal strategy (`decrossOpt`) solves edge-crossing minimization
 * exactly, which is NP-hard and has combinatorial blowup. The library throws a
 * guard error ("size of dag to decrossOpt is too large") once the graph grows
 * past what it can solve quickly. For graphs with more nodes than this
 * threshold we switch to the cheaper two-layer heuristic (`decrossTwoLayer`),
 * which scales to large graphs at the cost of slightly more edge crossings.
 *
 * The value is intentionally conservative. d3-dag's own limit is driven by the
 * widest layer rather than total node count, so we stay well below the point
 * where `decrossOpt` is at risk of throwing.
 */
const DECROSS_OPT_MAX_NODES = 30

/**
 * Returns the fill color for a job status.
 * Uses colors that match the app's badge variants.
 *
 * @param status - The job status.
 * @returns A hex color string.
 */
function getStatusColor(status: JobStatus): string {
  const variant = getStatusVariant(status)
  switch (variant) {
    case "default": // Running/Starting - primary color
      return "#18181b" // zinc-900 (matches default button)
    case "secondary": // Succeeded - muted green
      return "#166534" // green-800
    case "destructive": // Failed - red
      return "#dc2626" // red-600
    case "outline": // Pending states - amber
    default:
      return "#a16207" // amber-700
  }
}

/**
 * Formats array job status breakdown for tooltip display.
 *
 * @param arrayStatuses - Status counts for an array job.
 * @returns A formatted string like "RUNNING: 3, SUCCEEDED: 7", or undefined.
 */
function formatArrayStatusTooltip(arrayStatuses?: Record<JobStatus, number>): string | undefined {
  if (!arrayStatuses) return undefined
  const order: JobStatus[] = [
    JobStatus.SUCCEEDED,
    JobStatus.FAILED,
    JobStatus.RUNNING,
    JobStatus.STARTING,
    JobStatus.RUNNABLE,
    JobStatus.PENDING,
    JobStatus.SUBMITTED,
  ]
  const parts: string[] = []
  for (const status of order) {
    const count = arrayStatuses[status]
    if (count && count > 0) {
      parts.push(`${status}: ${count}`)
    }
  }
  return parts.length > 0 ? parts.join(", ") : undefined
}

/**
 * The computed layout result shared by both the d3-dag path and the manual
 * fallback: positioned nodes, positioned edges, and the overall SVG size.
 */
interface DagLayout {
  nodes: DagNode[]
  edges: DagEdge[]
  width: number
  height: number
}

/**
 * Computes a simple layered left-to-right layout without d3-dag.
 *
 * This is the fallback used when d3-dag's Sugiyama layout throws (for example
 * when the dependency graph contains a cycle, which d3-dag refuses to lay out).
 * It is intentionally cycle-tolerant: node depth is derived by iterative
 * relaxation with a hard cap on passes, so a cycle cannot cause an infinite
 * loop. Edges are preserved so the result is still a meaningful dependency
 * graph rather than a flat row of boxes.
 *
 * Coordinates are returned in the same "raw" space the d3-dag path uses (i.e.
 * before the PADDING offset applied at render time): x increases left-to-right
 * by depth, y increases top-to-bottom by stacking position within a column.
 *
 * @param jobs - The workflow jobs to lay out, each with its `dependsOn` list.
 * @returns A {@link DagLayout} with positioned nodes, edges, and SVG dimensions.
 */
function computeFallbackLayout({ jobs }: { jobs: HpcJob[] }): DagLayout {
  // Only consider dependencies that point at jobs we actually have, so a
  // dangling dependency id cannot distort the layout.
  const jobIds = new Set(jobs.map((j) => j.jobId))
  const parentsById = new Map<string, string[]>()
  for (const job of jobs) {
    parentsById.set(
      job.jobId,
      (job.dependsOn ?? []).filter((depId) => jobIds.has(depId)),
    )
  }

  // Compute each node's column as its longest-path depth from any root.
  // We relax depths iteratively: a node's depth is at least one more than the
  // max depth of its parents. The pass count is capped at the number of jobs,
  // which is enough to converge for any acyclic graph and bounds the work for
  // cyclic input (depths simply stop changing once the cap is hit).
  const depthById = new Map<string, number>()
  for (const job of jobs) depthById.set(job.jobId, 0)

  for (let pass = 0; pass < jobs.length; pass++) {
    let changed = false
    for (const job of jobs) {
      const parents = parentsById.get(job.jobId) ?? []
      let maxParentDepth = -1
      for (const parentId of parents) {
        maxParentDepth = Math.max(maxParentDepth, depthById.get(parentId) ?? 0)
      }
      const newDepth = maxParentDepth + 1
      if (newDepth > (depthById.get(job.jobId) ?? 0)) {
        depthById.set(job.jobId, newDepth)
        changed = true
      }
    }
    // No depth changed this pass, so we have converged.
    if (!changed) break
  }

  // Stack nodes vertically within each depth column, preserving input order.
  const nextRowByDepth = new Map<number, number>()
  const positionById = new Map<string, { x: number; y: number }>()
  const nodes: DagNode[] = []
  for (const job of jobs) {
    const depth = depthById.get(job.jobId) ?? 0
    const row = nextRowByDepth.get(depth) ?? 0
    nextRowByDepth.set(depth, row + 1)

    const x = depth * (NODE_WIDTH + HORIZONTAL_GAP)
    const y = row * (NODE_HEIGHT + VERTICAL_GAP)
    positionById.set(job.jobId, { x, y })
    nodes.push({ job, x, y })
  }

  // Build edges from each job to its (valid) dependents, using the positions
  // computed above. We iterate dependencies parent -> child to match the
  // arrow direction (dependency points to dependent).
  const edges: DagEdge[] = []
  for (const job of jobs) {
    const childPos = positionById.get(job.jobId)
    if (!childPos) continue
    for (const parentId of parentsById.get(job.jobId) ?? []) {
      const parentPos = positionById.get(parentId)
      if (!parentPos) continue
      edges.push({
        sourceId: parentId,
        targetId: job.jobId,
        sourceX: parentPos.x,
        sourceY: parentPos.y,
        targetX: childPos.x,
        targetY: childPos.y,
      })
    }
  }

  const maxX = Math.max(...nodes.map((n) => n.x), 0)
  const maxY = Math.max(...nodes.map((n) => n.y), 0)

  return {
    nodes,
    edges,
    width: maxX + NODE_WIDTH + PADDING * 2,
    height: maxY + NODE_HEIGHT + PADDING * 2,
  }
}

/**
 * WorkflowDAG component renders a directed acyclic graph visualization
 * of jobs in a workflow, showing dependencies between jobs.
 *
 * Uses d3-dag for automatic layout calculation (Sugiyama algorithm)
 * and renders the result as an SVG with React.
 *
 * The layout is horizontal (left-to-right):
 * - Root nodes (no dependencies) appear on the left
 * - Dependent jobs appear in columns to the right
 * - Arrows point from dependencies to dependents
 *
 * @param jobs - Array of HpcJob objects with dependsOn relationships.
 * @returns SVG visualization of the workflow DAG.
 */
export function WorkflowDAG({ jobs }: WorkflowDAGProps): JSX.Element {
  // Compute the DAG layout using d3-dag
  const { nodes, edges, width, height } = useMemo(() => {
    if (jobs.length === 0) {
      return { nodes: [], edges: [], width: 0, height: 0 }
    }

    // Build a set of valid job IDs for filtering invalid dependencies
    const jobIds = new Set(jobs.map((j) => j.jobId))

    // Create the DAG structure for d3-dag
    // Each node needs an id and parentIds (dependencies)
    const dagData = jobs.map((job) => ({
      id: job.jobId,
      // Filter to only include dependencies that exist in our job set
      parentIds: (job.dependsOn ?? []).filter((depId) => jobIds.has(depId)),
      job,
    }))

    try {
      // Use graphStratify to create the DAG from our data
      const stratify = graphStratify()
      const dag = stratify(dagData)

      // Select the decrossing (edge-crossing minimization) strategy by graph
      // size. decrossOpt is optimal but NP-hard and throws on large graphs;
      // decrossTwoLayer is a cheaper heuristic that scales. See
      // DECROSS_OPT_MAX_NODES for details.
      const decross =
        jobs.length <= DECROSS_OPT_MAX_NODES ? decrossOpt() : decrossTwoLayer()

      // Apply the Sugiyama layout algorithm
      // - decross: minimize edge crossings (strategy chosen above)
      // - coordCenter: center nodes within their layer
      // Note: d3-dag outputs vertical layout (y = depth), we swap coordinates below
      const layout = sugiyama()
        .decross(decross)
        .coord(coordCenter())
        .nodeSize(() => [NODE_HEIGHT + VERTICAL_GAP, NODE_WIDTH + HORIZONTAL_GAP])

      layout(dag)

      // Extract positioned nodes, swapping x/y for horizontal layout
      // d3-dag Sugiyama outputs: x = position across layer, y = depth
      // We want: x = depth (left-to-right), y = position (top-to-bottom)
      const positionedNodes: DagNode[] = []
      for (const node of dag.nodes()) {
        positionedNodes.push({
          job: node.data.job,
          // Swap coordinates: d3-dag's y (depth) becomes our x, d3-dag's x becomes our y
          x: node.y ?? 0,
          y: node.x ?? 0,
        })
      }

      // Extract edges with positions (also swapped)
      const positionedEdges: DagEdge[] = []
      for (const link of dag.links()) {
        positionedEdges.push({
          sourceId: link.source.data.id,
          targetId: link.target.data.id,
          // Swap coordinates for edges too
          sourceX: link.source.y ?? 0,
          sourceY: link.source.x ?? 0,
          targetX: link.target.y ?? 0,
          targetY: link.target.x ?? 0,
        })
      }

      // Calculate SVG dimensions based on swapped coordinates
      const maxX = Math.max(...positionedNodes.map((n) => n.x), 0)
      const maxY = Math.max(...positionedNodes.map((n) => n.y), 0)
      // Width now depends on max horizontal position (depth)
      const svgWidth = maxX + NODE_WIDTH + PADDING * 2
      // Height now depends on max vertical position (spread)
      const svgHeight = maxY + NODE_HEIGHT + PADDING * 2

      return {
        nodes: positionedNodes,
        edges: positionedEdges,
        width: svgWidth,
        height: svgHeight,
      }
    } catch (error) {
      // If d3-dag fails for a genuine reason (e.g. a dependency cycle), fall
      // back to a simple layered layout computed by hand. Unlike a flat
      // single-row layout, this preserves the dependency edges so the graph
      // stays readable. Nodes are placed in columns by their longest-path
      // depth from a root, and stacked vertically within each column.
      console.error("DAG layout failed, using fallback:", error)

      return computeFallbackLayout({ jobs })
    }
  }, [jobs])

  if (jobs.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground bg-muted rounded-md border">
        No jobs in workflow
      </div>
    )
  }

  /**
   * Generates an SVG path for a curved edge between two nodes.
   * Uses a cubic bezier curve for smooth horizontal connections.
   * Edge goes from right side of source node to left side of target node.
   *
   * @param edge - The edge with source and target positions.
   * @returns SVG path string for the bezier curve.
   */
  const generateEdgePath = (edge: DagEdge): string => {
    // Start from right side of source node
    const x1 = edge.sourceX + PADDING + NODE_WIDTH
    const y1 = edge.sourceY + PADDING + NODE_HEIGHT / 2
    // End at left side of target node
    const x2 = edge.targetX + PADDING
    const y2 = edge.targetY + PADDING + NODE_HEIGHT / 2
    
    // Control points for horizontal bezier curve
    // Midpoint x creates a smooth S-curve for connections
    const midX = (x1 + x2) / 2
    
    return `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`
  }

  return (
    <div className="bg-muted/50 border rounded-md overflow-x-auto p-4">
      <svg
        width={width}
        height={height}
        className="block min-w-full"
        role="img"
        aria-label="Workflow dependency graph"
      >
        {/* Arrow marker definition (scaled to ~70% of original size) */}
        <defs>
          <marker
            id="dag-arrowhead"
            markerWidth="7"
            markerHeight="5"
            refX="6"
            refY="2.5"
            orient="auto"
          >
            <polygon points="0 0, 7 2.5, 0 5" fill="#9ca3af" />
          </marker>
        </defs>

        {/* Draw edges */}
        {edges.map((edge) => (
          <path
            key={`${edge.sourceId}-${edge.targetId}`}
            d={generateEdgePath(edge)}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="2"
            markerEnd="url(#dag-arrowhead)"
          />
        ))}

        {/* Draw nodes */}
        {nodes.map((node) => {
          const x = node.x + PADDING
          const y = node.y + PADDING
          const statusColor = getStatusColor(node.job.status)
          
          // Build node label with array size if applicable
          const arrayLabel = node.job.arraySize ? ` [${node.job.arraySize}]` : ""
          const fullLabel = node.job.jobName + arrayLabel
          const maxLen = 18 - arrayLabel.length
          const displayLabel =
            node.job.jobName.length > maxLen
              ? node.job.jobName.substring(0, maxLen - 2) + "..." + arrayLabel
              : fullLabel

          // Build tooltip
          const tooltipParts: string[] = [node.job.jobName]
          const arrayStatusTooltip = formatArrayStatusTooltip(node.job.arrayStatuses)
          if (arrayStatusTooltip) tooltipParts.push(arrayStatusTooltip)
          if (node.job.statusReason) tooltipParts.push(node.job.statusReason)
          const tooltip = tooltipParts.join("\n")

          return (
            <g key={node.job.jobId}>
              <rect
                x={x}
                y={y}
                width={NODE_WIDTH}
                height={NODE_HEIGHT}
                rx="6"
                fill={statusColor}
                stroke={statusColor}
                strokeWidth="2"
              >
                <title>{tooltip}</title>
              </rect>
              <text
                x={x + NODE_WIDTH / 2}
                y={y + NODE_HEIGHT / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="12"
                fill="white"
                className="pointer-events-none font-sans"
              >
                {displayLabel}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
